import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { wipAiFixture, formatCurrency, shortMonth } from '../mocks/wipAiFixture'
import { I } from './Icons'

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 5, 25, 0.45);
  z-index: 150;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 20px;
  overflow-y: auto;
`

const Modal = styled.div`
  background: ${p => p.theme.colors.semantic.surface};
  width: 100%;
  max-width: 780px;
  border-radius: 12px;
  box-shadow: ${p => p.theme.colors.semantic.shadowLg};
  overflow: hidden;
`

const ModalHd = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
  gap: 12px;
`

const ModalT = styled.span`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`

const CloseBtn = styled.button`
  margin-left: auto;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid ${p => p.theme.colors.semantic.border};
  background: ${p => p.theme.colors.semantic.surface};
  display: grid;
  place-items: center;
  color: ${p => p.theme.colors.semantic.textMuted};
  cursor: pointer;
`

const ModalBd = styled.div`
  padding: 32px 40px;
  max-height: 70vh;
  overflow-y: auto;
`

const Sheet = styled.div`
  font-family: Arial, sans-serif;
  color: #111;
  font-size: 12px;
  line-height: 1.55;
  h1 {
    font-family: ${p => p.theme.fontFamily.sans.join(', ')};
    font-size: 20px;
    letter-spacing: -0.02em;
    margin: 0 0 4px;
  }
  h3 {
    font-family: ${p => p.theme.fontFamily.sans.join(', ')};
    font-size: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin: 18px 0 6px;
    border-bottom: 1px solid #000;
    padding-bottom: 3px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 10px 0;
    font-size: 12px;
  }
  th,
  td {
    border: 1px solid #999;
    padding: 6px 8px;
    text-align: left;
  }
  th {
    background: #eee;
    font-family: ${p => p.theme.fontFamily.sans.join(', ')};
    font-size: 11px;
    letter-spacing: 0.04em;
  }
  td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`

const Hdr = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  border-bottom: 2px solid #000;
  padding-bottom: 10px;
  margin-bottom: 14px;
`

const Logo = styled.div`
  width: 90px;
  height: 36px;
  background: ${p => p.theme.colors.black};
  color: #fff;
  display: grid;
  place-items: center;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-weight: 700;
  letter-spacing: -0.02em;
  font-size: 12px;
`

const WipAiPreviewModal = ({ rev, onClose }) => {
  const p = wipAiFixture.project
  const r = rev.snapshot.wipData
  const monthlyReports = wipAiFixture.wipReport.monthlyWipReports
  const idx = monthlyReports.findIndex(m => m.month === wipAiFixture.currentMonth)
  const threeMonths = monthlyReports.slice(idx, idx + 3)

  const orationHtml = (rev.snapshot.oration || '')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')

  return (
    <Scrim
      onClick={e => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="presentation"
    >
      <Modal role="dialog" aria-modal="true" aria-labelledby="wip-ai-preview-title">
        <ModalHd>
          <ModalT id="wip-ai-preview-title">Preview — Export .docx</ModalT>
          <span style={{ fontSize: 12, color: '#676767', marginLeft: 12 }}>
            Rev {rev.revisionNumber} · {rev.caption}
          </span>
          <CloseBtn type="button" onClick={onClose} aria-label="Close">
            <I.x width={14} height={14} />
          </CloseBtn>
        </ModalHd>
        <ModalBd>
          <Sheet>
            <Hdr>
              <Logo>KEYSTONE</Logo>
              <div style={{ flex: 1 }}>
                <h1>Work in Progress Report</h1>
                <div>Reporting Period: April 1 – April 22, 2026</div>
              </div>
            </Hdr>
            <div className="bill">
              <strong>Bill to:</strong> {p.client}
              <br />
              {p.Address}
              <br />
              Matter: {p.name} · {p.matterNumber}
            </div>
            <h3>Summary</h3>
            <div
              style={{ whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{ __html: orationHtml }}
            />
            <h3>Monthly Summary Financials</h3>
            <table>
              <thead>
                <tr>
                  <th />
                  {threeMonths.map(m => (
                    <th key={m.month} className="num">
                      {shortMonth(m.month)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Staff Fees</td>
                  {threeMonths.map(m => (
                    <td key={m.month} className="num">
                      {m.submittedHours.staffRate
                        ? formatCurrency(m.submittedHours.staffRate)
                        : '—'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Expenses</td>
                  {threeMonths.map(m => (
                    <td key={m.month} className="num">
                      {m.submittedExpenses.staffExpenses
                        ? formatCurrency(m.submittedExpenses.staffExpenses)
                        : '—'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>
                    <strong>Total</strong>
                  </td>
                  {threeMonths.map(m => (
                    <td key={m.month} className="num">
                      <strong>
                        {m.totalFeesAndExpenses ? formatCurrency(m.totalFeesAndExpenses) : '—'}
                      </strong>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Budget</td>
                  {threeMonths.map(m => (
                    <td key={m.month} className="num">
                      {formatCurrency(m.budget)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            <h3>Budget Trend</h3>
            <div>
              <strong>{rev.snapshot.budgetTrend}</strong> — actuals-to-date{' '}
              {formatCurrency(r.totalFeesAndExpenses)} of {formatCurrency(r.budget)} budget.
            </div>
            <h3>Current Staff Summary</h3>
            <table>
              <thead>
                <tr>
                  <th>Person</th>
                  <th className="num">Hours</th>
                  <th className="num">Fees</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const by = {}
                  for (const h of r.hoursDetails) {
                    if (!by[h.personName]) by[h.personName] = { hours: 0, total: 0 }
                    by[h.personName].hours += h.hours
                    by[h.personName].total += h.total || 0
                  }
                  return Object.entries(by).map(([n, v]) => (
                    <tr key={n}>
                      <td>{n}</td>
                      <td className="num">{v.hours.toFixed(1)}</td>
                      <td className="num">{formatCurrency(v.total)}</td>
                    </tr>
                  ))
                })()}
              </tbody>
            </table>
            <h3>Projected Case Schedule</h3>
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {rev.snapshot.keyDates.map((k, i) => (
                  <tr key={i}>
                    <td>
                      {k.Name} — {k.Description__c}
                    </td>
                    <td>{k.StartDate__c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Sheet>
        </ModalBd>
      </Modal>
    </Scrim>
  )
}

WipAiPreviewModal.propTypes = {
  rev: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default WipAiPreviewModal
